import { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { AppContext } from "../contexts/AppContext";
import useFetchMedia from "../hooks/useFetchMedia";
import { MEDIA_PREFIX } from "../Settings";
import HandIcon from "../assets/icon_hand.svg?react";

interface ScreensaverItemProps {
  mediaId: number;
  onEnded: () => void;
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Wrapper = styled.div<{ show: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background-color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: none;
  transition: opacity 2s ease-in-out, transform 2s ease-in-out;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transform: ${({ show }) => (show ? "scale(1)" : "scale(1.25)")};
`;


const FadeWrapper = styled.div<{ show: boolean }>`
  width: 100%;
  height: 100%;
  opacity: 0;
  animation: ${fadeIn} 1s ease-in-out forwards;
  ${({ show }) => show && css`opacity: 1;`}
  transition: opacity 1s ease-in-out;
  position: absolute;
  top: 0;
  left: 0;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const InteractionSymbol = styled.div`
  position: absolute;
  left: 50%;
  top: 70%;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: ${p => p.theme.backgroundColor};
  transform: translateX(-50%);
  transition: top .5s;
  box-shadow: 0 0px 92px rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;

  & svg {
    width: 100px;
    height: 100px;
    fill: ${p => p.theme.textColor};
  }
`

const ScreensaverItem: FC<ScreensaverItemProps> = ({ mediaId, onEnded }) => {
  const [media] = useFetchMedia(mediaId, { dimension: "3000" });
  const timeoutRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const mediaType = media?.media_type;
  const mediaUrl = media ? `${MEDIA_PREFIX}${media.url}` : "";
  const [show, setShow] = useState(false);

  useEffect(() => {
    setShow(true); // trigger fade-in
    if (mediaType === "image") {
      timeoutRef.current = setTimeout(onEnded, 20000);
    }
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [mediaType, onEnded]);

  const handleError = () => {
    console.warn("Media failed to load:", mediaUrl);
    onEnded();
  };

  if (!media) return null;

  return (
    <FadeWrapper show={show}>
      {(mediaType === "image" || mediaType === "dm") && <Image src={mediaUrl} alt="Screensaver" />}
      {(mediaType === "video" || mediaType === "audio") && (
        <Video src={mediaUrl} muted autoPlay playsInline onEnded={onEnded} onError={handleError} />
      )}
    </FadeWrapper>
  );
};

const ScreensaverView: FC = () => {
  const { deviceData, setShowScreensaver, previewIsActive } = useContext(AppContext);
  const mediaList = deviceData?.idleMode?.media || [];
  const [mediaIndex, setMediaIndex] = useState<number>(0);
  const [makeScreensaverVisible, setMakeScreensaverVisible] = useState(false);

  useEffect(() => {
    if (!deviceData?.idleMode?.screensaver || mediaList.length === 0) {
      setShowScreensaver(false);
    }
  }, [deviceData, mediaList.length, setShowScreensaver]);

  // Fade in on start
  useEffect(() => {
  const timeout = setTimeout(() => { setMakeScreensaverVisible(true) }, 10);
  return () => clearTimeout(timeout);
}, []);

  // Show next media item on video end
  const handleMediaEnd = () => {
    setMediaIndex(prev => (prev + 1) % mediaList.length);
  };

  // Fade out on end, before destroying this view
  const hideScreensaver = useCallback(() => {
    setMakeScreensaverVisible(false);
    setTimeout(() => {
      setShowScreensaver(false)
    }, 2000);
  }, [setShowScreensaver]);

  if (
    deviceData?.idleMode?.screensaver !== "enabled" ||
    mediaList.length === 0 ||
    previewIsActive
  ) {
    return null;
  }

  return (
    <Wrapper onClick={hideScreensaver} onTouchStart={hideScreensaver} show={makeScreensaverVisible}>
      <ScreensaverItem
        key={mediaList[mediaIndex]} // trigger re-mount for each item
        mediaId={mediaList[mediaIndex]}
        onEnded={handleMediaEnd}
      />
      <InteractionSymbol className="hand">
        <HandIcon/>
      </InteractionSymbol>
    </Wrapper>
  );
};

export default ScreensaverView;
